import { Head, router, useForm } from "@inertiajs/react"
import { Building2 } from "lucide-react"
import { useState } from "react"

import { AccountStateBadge } from "@/components/accounts/account-state-badge"
import { AccountDeleteMenu } from "@/components/partner-accounts/account-delete-menu"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Empty } from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import PartnerLayout from "@/layouts/partner-layout"
import { formatCurrency, formatRelativeTime } from "@/lib/utils"
import { partnerAccountPath, partnerAccountsPath } from "@/routes"

interface Account {
  uid: string
  name: string
  industry: string | null
  state: string
  agent_count: number
  mrr_cents: number
  last_activity_at: string | null
  created_at: string
  deletable?: boolean
}

export default function AccountsIndex({ accounts }: { accounts: Account[] }) {
  const [addOpen, setAddOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/unbound-method -- Inertia useForm returns closures
  const { data, setData, post, processing, reset } = useForm({
    account: { name: "" },
  })

  function handleCreate() {
    post(partnerAccountsPath(), {
      onSuccess: () => {
        setAddOpen(false)
        reset()
      },
    })
  }

  return (
    <PartnerLayout>
      <Head title="Accounts" />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl/8 font-semibold">Accounts</h1>
          <Button
            size="sm"
            className="rounded-sm"
            onClick={() => setAddOpen(true)}
          >
            Add account
          </Button>
        </div>

        <Card className="gap-4 border-0 shadow-none">
          <CardContent className="p-0">
            {accounts.length === 0 ? (
              <Empty
                icon={<Building2 className="h-5 w-5" />}
                title="No accounts yet"
                description="Add your first account to start deploying agents and billing your customers."
              >
                <Button
                  size="sm"
                  className="rounded-sm"
                  onClick={() => setAddOpen(true)}
                >
                  Add account
                </Button>
              </Empty>
            ) : (
              <div className="overflow-x-auto">
                <table className="text-foreground min-w-full text-left text-sm/6">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="border-b px-4 py-2 font-medium first:pl-1 last:pr-1">
                        Account
                      </th>
                      <th className="border-b px-4 py-2 text-right font-medium first:pl-1 last:pr-1">
                        MRR
                      </th>
                      <th className="border-b px-4 py-2 text-right font-medium first:pl-1 last:pr-1">
                        Agents
                      </th>
                      <th className="border-b px-4 py-2 text-right font-medium first:pl-1 last:pr-1">
                        Last active
                      </th>
                      <th className="border-b px-4 py-2 text-right font-medium first:pl-1 last:pr-1">
                        Status
                      </th>
                      <th className="border-b px-4 py-2 first:pl-1 last:pr-1">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => (
                      <tr
                        key={account.uid}
                        className="hover:bg-muted/50 cursor-pointer"
                        onClick={() =>
                          router.visit(partnerAccountPath({ id: account.uid }))
                        }
                      >
                        <td className="border-b px-4 py-4 first:pl-1 last:pr-1">
                          <span className="font-medium underline-offset-4 hover:underline">
                            {account.name}
                          </span>
                        </td>
                        <td className="border-b px-4 py-4 text-right tabular-nums first:pl-1 last:pr-1">
                          {account.mrr_cents > 0
                            ? formatCurrency(account.mrr_cents / 100)
                            : "—"}
                        </td>
                        <td className="border-b px-4 py-4 text-right tabular-nums first:pl-1 last:pr-1">
                          {account.agent_count > 0 ? account.agent_count : "—"}
                        </td>
                        <td className="border-b px-4 py-4 text-right first:pl-1 last:pr-1">
                          {formatRelativeTime(
                            account.last_activity_at ?? undefined,
                          )}
                        </td>
                        <td className="border-b px-4 py-4 text-right first:pl-1 last:pr-1">
                          <AccountStateBadge state={account.state} />
                        </td>
                        <td className="border-b px-4 py-4 text-right first:pl-1 last:pr-1">
                          <AccountDeleteMenu account={account} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          setAddOpen(open)
          if (!open) reset()
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add account</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 pt-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="company-name">Company name</Label>
              <Input
                id="company-name"
                placeholder="e.g. Acme Corp"
                value={data.account.name}
                onChange={(e) =>
                  setData("account", {
                    ...data.account,
                    name: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => setAddOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="rounded-sm"
              disabled={!data.account.name.trim() || processing}
              loading={processing}
              onClick={handleCreate}
            >
              Add account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PartnerLayout>
  )
}
